import * as Sentry from "@sentry/react";
import axios, { AxiosError, AxiosResponse } from "axios";

const GET_TOKEN_TIMEOUT_MS = 10000;
const MAX_WAF_CHALLENGE_RETRIES = 3;

const rejectAsServerError = (
    response: AxiosResponse,
    wafAction: string,
): Promise<never> => {
    const message = `AWS WAF ${wafAction} response blocked the request`;

    Sentry.captureMessage(message, {
        level: "warning",
        extra: {
            url: response.config.url,
            method: response.config.method,
            originalStatus: response.status,
            wafAction,
            wafActionNormalized: wafAction.toLowerCase(),
            wafChallengeRetryCount: response.config.wafChallengeRetryCount ?? 0,
        },
    });

    return Promise.reject(
        new AxiosError(
            message,
            AxiosError.ERR_BAD_RESPONSE,
            response.config,
            response.request,
            { ...response, status: 500, statusText: "Internal Server Error" },
        ),
    );
};

const handleWafResponse = async (
    response: AxiosResponse,
    wafAction: string,
) => {
    const awsWafIntegration = window.AwsWafIntegration;
    const retryCount = response.config.wafChallengeRetryCount ?? 0;
    const isSolvableChallenge =
        wafAction.toLowerCase() === "challenge" &&
        retryCount < MAX_WAF_CHALLENGE_RETRIES &&
        !!awsWafIntegration;

    if (!isSolvableChallenge) {
        return rejectAsServerError(response, wafAction);
    }

    try {
        await Promise.race([
            awsWafIntegration.getToken(),
            new Promise((_, reject) =>
                setTimeout(
                    () => reject(new Error("getToken timed out")),
                    GET_TOKEN_TIMEOUT_MS,
                ),
            ),
        ]);
    } catch {
        return rejectAsServerError(response, wafAction);
    }

    Sentry.captureMessage("AWS WAF challenge solved before retrying", {
        level: "info",
        extra: {
            url: response.config.url,
            method: response.config.method,
            wafChallengeRetryCount: retryCount + 1,
        },
    });

    return axios.request({
        ...response.config,
        wafChallengeRetryCount: retryCount + 1,
    });
};

axios.interceptors.response.use(
    (response) => {
        const wafAction = response.headers?.["x-amzn-waf-action"];
        if (!wafAction) {
            return response;
        }

        return handleWafResponse(response, String(wafAction));
    },
    (error: unknown) => {
        if (!axios.isAxiosError(error) || !error.response) {
            return Promise.reject(error);
        }

        const wafAction = error.response.headers?.["x-amzn-waf-action"];
        if (!wafAction) {
            return Promise.reject(error);
        }

        return handleWafResponse(error.response, String(wafAction));
    },
);
